import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { RiShieldCrossLine, RiArrowLeftLine, RiHomeLine, RiLoginBoxLine } from 'react-icons/ri';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { resolveLandingRoute } from '../../config/navigation';
import { buildMenuAccessChecker } from '../../utils/permissions';

/**
 * AccessDenied Page
 *
 * หน้าที่แสดงเมื่อ user พยายามเข้าถึง route ที่ไม่มีสิทธิ์
 *
 * P-S616-1: เดิมหน้านี้เป็น full-screen ไม่มี sidebar และปุ่ม "กลับหน้าหลัก" ชี้ `/dashboard`
 * ตายตัว → role ที่ไม่มี `dashboard.view` กดแล้ววนกลับมาที่หน้าเดิม ไม่มีทางออกนอกจากพิมพ์ URL
 * เอง (dead-end จริง). ตอนนี้ render ใน Layout เพื่อให้มีเมนูให้หนีเสมอ และปุ่มชี้ไปเมนูแรก
 * ที่ role นั้นเปิดได้จริง
 */
const AccessDenied: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, permissions } = useAuth();
  const { isSuperAdmin, isSuperUser } = usePermission();
  const fromPath = (location.state as any)?.from?.pathname;

  // ปลายทาง "กลับหน้าหลัก" = เมนูแรกที่ผู้ใช้คนนี้เข้าได้จริง (ไม่ใช่ /dashboard ตายตัว)
  const homeTarget = resolveLandingRoute(
    buildMenuAccessChecker(permissions, isSuperAdmin || isSuperUser)
  );
  // ถ้าเมนูที่เข้าได้คือหน้าเดิมที่เพิ่งโดนปฏิเสธ การกดปุ่มจะวนที่เดิม → ซ่อนปุ่มไปเลย
  const canOfferHome = homeTarget !== location.pathname && homeTarget !== fromPath;

  const card = (
    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
      {/* Icon */}
      <div className="flex justify-center mb-6">
        <div className="w-20 h-20 bg-danger-100 rounded-full flex items-center justify-center">
          <RiShieldCrossLine className="w-12 h-12 text-danger-600" />
        </div>
      </div>

      {/* Title */}
      <h1 className="text-3xl font-bold text-danger-600 text-center mb-4">
        ไม่มีสิทธิ์เข้าถึง
      </h1>

      {/* Description */}
      <p className="text-secondary-600 text-center mb-2">
        คุณไม่มีสิทธิ์เข้าถึงหน้านี้
      </p>
      <p className="text-secondary-500 text-sm text-center mb-8">
        {isAuthenticated
          ? 'กรุณาเลือกเมนูอื่นจากแถบด้านซ้าย หรือติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์การเข้าถึง'
          : 'กรุณาเข้าสู่ระบบก่อนใช้งาน'}
      </p>

      {/* Path info (เฉพาะ development) */}
      {import.meta.env.MODE === 'development' && fromPath && (
        <div className="bg-warning-50 border border-warning-200 rounded-md p-3 mb-6">
          <p className="text-xs text-warning-700">
            <strong>พยายามเข้าถึง:</strong> {fromPath}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        {isAuthenticated ? (
          <>
            {/* ปุ่มกลับหน้าที่แล้ว */}
            <button
              onClick={() => navigate(-1)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-md font-medium transition-colors duration-150"
            >
              <RiArrowLeftLine className="w-5 h-5" />
              กลับหน้าที่แล้ว
            </button>

            {/* ปุ่มไปหน้าแรกที่เข้าถึงได้ */}
            {canOfferHome && (
              <button
                onClick={() => navigate(homeTarget, { replace: true })}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-150"
              >
                <RiHomeLine className="w-5 h-5" />
                ไปหน้าที่เข้าถึงได้
              </button>
            )}
          </>
        ) : (
          <Link
            to="/login"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-150"
          >
            <RiLoginBoxLine className="w-5 h-5" />
            เข้าสู่ระบบ
          </Link>
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-secondary-200">
        <p className="text-xs text-secondary-500 text-center">
          หากคุณคิดว่านี่เป็นข้อผิดพลาด กรุณาติดต่อผู้ดูแลระบบ
        </p>
      </div>
    </div>
  );

  // ยังไม่ login → ไม่มี sidebar ให้แสดงอยู่แล้ว คง full-screen เดิมไว้
  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-secondary-50 flex items-center justify-center p-4" aria-label="ไม่มีสิทธิ์เข้าถึง">
        {card}
      </main>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-center p-4" aria-label="ไม่มีสิทธิ์เข้าถึง">
        {card}
      </div>
    </Layout>
  );
};

export default AccessDenied;
